import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import CloseButton from "../CloseButton/CloseButton";
import { userRoles } from "../../helpers/constants";
import "./MoveFolderConfirmModal.css";

// ADMIN сильнее EDITOR сильнее COMMENTER сильнее VIEWER
const ROLE_ORDER = ["ADMIN", "EDITOR", "COMMENTER", "VIEWER"];
const isDowngrade = (before, after) =>
  ROLE_ORDER.indexOf(after) > ROLE_ORDER.indexOf(before);

const LIST_LIMIT = 8;

const roleName = (role) => userRoles[role] || role;

const PersonRow = ({ person, children }) => (
  <li className="move-folder-person">
    <span className="move-folder-person-name">
      {person.name || person.email}
      {person.currentUser && <span className="move-folder-you"> (вы)</span>}
    </span>
    {person.name && person.email && (
      <span className="move-folder-person-email">{person.email}</span>
    )}
    {person.pending && (
      <span className="move-folder-person-email">приглашение не принято</span>
    )}
    <span className="move-folder-person-role">{children}</span>
  </li>
);

const Section = ({ title, people, renderRole }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!people.length) return null;
  const shown = isExpanded ? people : people.slice(0, LIST_LIMIT);
  return (
    <section className="move-folder-section">
      <h3 className="move-folder-section-title">
        {title} <span className="move-folder-count">{people.length}</span>
      </h3>
      <ul className="move-folder-list">
        {shown.map((person) => (
          <PersonRow
            key={person.userId != null ? `u${person.userId}` : `e${person.email}`}
            person={person}
          >
            {renderRole(person)}
          </PersonRow>
        ))}
      </ul>
      {people.length > shown.length && (
        <button
          className="move-folder-more"
          onClick={() => setIsExpanded(true)}
        >
          Показать ещё {people.length - shown.length}
        </button>
      )}
    </section>
  );
};

/**
 * Подтверждение переноса папки, когда меняется, кто её видит.
 * preview — ответ GET /api/v1/project/{id}/move/preview.
 * onConfirm(keepAccess) — перенести; keepAccess — оставить доступ тем, кто его теряет.
 */
const MoveFolderConfirmModal = ({
  folderTitle,
  targetTitle,
  isRoot,
  preview,
  isBusy,
  onConfirm,
  onClose,
}) => {
  const [keepAccess, setKeepAccess] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const losing = preview.losing || [];
  const gaining = preview.gaining || [];
  const changing = preview.changing || [];
  const lostLinks = preview.foldersWithLostShareLinks || [];
  const downgraded = changing.filter((p) =>
    isDowngrade(p.roleBefore, p.roleAfter)
  );
  const canKeep = losing.length > 0 || downgraded.length > 0;
  const iLoseAccess = losing.some((p) => p.currentUser) && !keepAccess;

  const destination = isRoot
    ? "в корень пространства"
    : `в «${targetTitle}»`;

  const modal = (
    <div
      className="move-folder-overlay"
      onClick={(e) => {
        // карточки ловят клики своих детей (порталы тоже) — не даём открыть папку
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="move-folder-modal" role="dialog" aria-modal="true">
        <CloseButton
          onClose={onClose}
          styles={{ position: "absolute", top: 20, right: 20 }}
        />
        <h2 className="move-folder-title">
          Переместить «{folderTitle}» {destination}?
        </h2>
        <p className="move-folder-lead">
          Доступ к папке, всем вложенным папкам и видео будет определяться новым
          местом.
          {keepAccess
            ? " Те, кто теряет доступ, сохранят его с прежней ролью."
            : " Вот что изменится:"}
        </p>

        <div className="move-folder-body">
          <Section
            title="Потеряют доступ"
            people={losing}
            renderRole={(p) => (
              <>
                {roleName(p.roleBefore)}
                {keepAccess && (
                  <span className="move-folder-kept"> — сохранится</span>
                )}
              </>
            )}
          />
          <Section
            title="Получат доступ"
            people={gaining}
            renderRole={(p) => roleName(p.roleAfter)}
          />
          <Section
            title="Изменится роль"
            people={changing}
            renderRole={(p) => (
              <>
                {roleName(p.roleBefore)} → {roleName(p.roleAfter)}
                {keepAccess && isDowngrade(p.roleBefore, p.roleAfter) && (
                  <span className="move-folder-kept">
                    {" "}
                    — останется {roleName(p.roleBefore).toLowerCase()}
                  </span>
                )}
              </>
            )}
          />
          {lostLinks.length > 0 && (
            <section className="move-folder-section">
              <h3 className="move-folder-section-title">Ссылки-приглашения</h3>
              <p className="move-folder-note">
                Ссылки на {lostLinks.map((f) => `«${f.title}»`).join(", ")}{" "}
                больше не будут открывать эту папку. Тех, кто уже вошёл по ним,
                касается список выше.
              </p>
            </section>
          )}
        </div>

        {canKeep && (
          <label className="move-folder-keep">
            <input
              type="checkbox"
              checked={keepAccess}
              onChange={(e) => setKeepAccess(e.target.checked)}
            />
            <span>
              Сохранить доступ тем, кто его теряет
              <span className="move-folder-keep-hint">
                Им будет выдана роль прямо на эту папку
              </span>
            </span>
          </label>
        )}

        {iLoseAccess && (
          <p className="move-folder-warning">
            Вы тоже потеряете доступ к этой папке.
          </p>
        )}

        <div className="move-folder-actions">
          <button className="move-folder-cancel" onClick={onClose}>
            Отмена
          </button>
          <PrimaryButton
            isLoading={isBusy}
            onClick={() => onConfirm(keepAccess)}
            text="Переместить"
          />
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default MoveFolderConfirmModal;
